import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Link as MUILink,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import MainLayout from '../../layouts/MainLayout';
import { useAuthProvider } from '../../providers/AuthProvider';
import login from '../../services/api/users/login';
import ConnectWithButtons from './ConnectWithButtons';

const useStyles = makeStyles((theme) => ({
    submitButton: {
        marginTop: theme.spacing(3),
    },
}));

const LoginForm = ({ previousLocation }) => {
    const router = useRouter();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const { isLoggedIn, refetchUser, refetchOrganization } = useAuthProvider();
    const [isLoading, setIsLoading] = useState(true);
    const classes = useStyles();

    useEffect(() => {
        if (isLoggedIn) {
            router.replace('/analyses');
        } else {
            setIsLoading(false);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (router?.query?.githubAuthError) {
            setSnackbarOpen(true);
            setSnackbarMessage(
                'There is an error with your Github Authentication process. Please try again or contact us.'
            );
        }
    }, [router?.query?.githubAuthError]);

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const onSubmit = async (formValues) => {
        setSnackbarOpen(false);
        setIsLoading(true);
        try {
            await login(formValues);
            refetchUser();
            refetchOrganization();
            router.push(previousLocation || '/analyses');
        } catch (e) {
            setSnackbarOpen(true);
            setSnackbarMessage(
                'Your email or password is wrong. Please try again or contact us.'
            );
            setIsLoading(false);
        }
    };

    return (
        <MainLayout loader={isLoading}>
            <Box
                sx={{
                    my: 8,
                    mx: 'auto',
                    width: '90%',
                    maxWidth: '450px',
                }}
            >
                <Typography variant="h2" gutterBottom>
                    Log in to GreenFrame
                </Typography>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert severity="error" onClose={handleClose} variant="filled">
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{
                        mt: 4,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Controller
                        name="email"
                        type="email"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Email is required',
                            },
                            pattern: {
                                value: /\S+@\S+\.\S+/,
                                message: 'Email must have a valid format',
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                name="email"
                                type="email"
                                label="Email"
                                variant="outlined"
                                color="secondary"
                                required={true}
                                size="small"
                                error={!!errors.email}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                    <Controller
                        name="password"
                        type="password"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Password is required',
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                name="password"
                                type="password"
                                label="Password"
                                variant="outlined"
                                color="secondary"
                                required={true}
                                size="small"
                                error={!!errors.password}
                                helperText={fieldState.error?.message}
                                sx={{ mt: 2 }}
                            />
                        )}
                    />

                    <Controller
                        name="rememberMe"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Checkbox {...field} color="secondary" />}
                                label="Remember me"
                                labelPlacement="end"
                            />
                        )}
                    />

                    {errors.password && (
                        <Alert severity="error" className={classes.alert}>
                            {errors.password.message}
                        </Alert>
                    )}

                    <Button
                        color="primary"
                        type="submit"
                        variant="contained"
                        sx={{ mt: 3 }}
                    >
                        Log in
                    </Button>
                </Box>
                <ConnectWithButtons githubButtonLabel="Login with Github" />
                <Box sx={{ mt: 1, mx: 'auto', textAlign: 'center' }}>
                    <Typography variant="subtitle1">
                        Don&apos;t have an account yet?
                    </Typography>
                    <Link passHref href="/register">
                        <MUILink color="secondary">Create an account for free</MUILink>
                    </Link>
                </Box>
            </Box>
        </MainLayout>
    );
};

LoginForm.propTypes = {
    previousLocation: PropTypes.string,
    onSubmit: PropTypes.func,
};

export default LoginForm;
